import React, { useState, useEffect, useMemo, useRef } from "react"

const TAG_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-green-100 text-green-700",
  "bg-purple-100 text-purple-700",
  "bg-orange-100 text-orange-700",
  "bg-pink-100 text-pink-700",
  "bg-teal-100 text-teal-700",
  "bg-yellow-100 text-yellow-800",
  "bg-red-100 text-red-700",
]

const getTagColor = (tag) => {
  let hash = 0
  for (let i = 0; i < tag.length; i++) hash = (hash * 31 + tag.charCodeAt(i)) | 0
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length]
}
import { navigate } from "vike/client/router"
import {
  EyeIcon,
  PlusCircleIcon,
  TrashIcon,
  XMarkIcon,
  ShieldExclamationIcon,
  TagIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline"
import Icon from "aldoo-ra/Icon"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import Checkbox from "aldoo-ra/Checkbox"
import Value from "aldoo-ra/Value"
import Tooltip from "aldoo-ra/Tooltip"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import AssignDialog from "./AssignDialog"
import AddTicketDialog from "./AddTicketDialog"
import RestoreTicketsDialog from "./RestoreTicketsDialog"
import SpamTicketsDialog from "./SpamTicketsDialog"
import TagDialog from "./TagDialog"
import MergeTickets from "./MergeTickets"
import DateFilterDropdown from "aldoo-ra/DateFilterDropdown"
import {
  GetTickets,
  GetTicketDetails,
  GetAgents,
  GetTags,
  DeleteTickets,
  UpdateTicket,
} from "./api"
import { twMerge } from "tailwind-merge"
import { formatDate, getRelativeTimeString } from "./time-format"
import moment from "moment"

export default function TicketBrowser({ onSelectTicket }) {
  // Track URL changes
  const prevUrlRef = useRef("")

  // State for URL parameter handling
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)

  // Core state
  const [refreshKey, setRefreshKey] = useState(0)
  const [selectedItems, setSelectedItems] = useState([])
  const [selectedTicketData, setSelectedTicketData] = useState([])

  // Filter states
  const [statusFilter, setStatusFilter] = useState([
    { value: null, label: "All" },
  ])
  const [assignmentFilter, setAssignmentFilter] = useState([
    { value: null, label: "All Agents" },
  ])
  const [pendingReplyFilter, setPendingReplyFilter] = useState([
    { value: null, label: "All" },
  ])
  const [tagFilter, setTagFilter] = useState([])
  const [dateFilter, setDateFilter] = useState({ type: "preset", value: null }) // Lifetime by default

  // Data states
  const [agents, setAgents] = useState([])
  const [tags, setTags] = useState([])

  // Dialog states
  const [assignDialog, setAssignDialog] = useState({
    isOpen: false,
    ticketId: null,
    ticketLabel: "",
    currentAssignee: null,
  })
  const [addTicketDialog, setAddTicketDialog] = useState({
    isOpen: false,
  })
  const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false)
  const [isSpamDialogOpen, setIsSpamDialogOpen] = useState(false)
  const [tagDialog, setTagDialog] = useState({
    isOpen: false,
    ticketId: null,
    ticketLabel: "",
    currentTags: [],
    ticketIds: [],
    ticketLabels: [],
  })
  const [mergeDialog, setMergeDialog] = useState({
    isOpen: false,
    initialTicketId: null,
    initialTicketLabel: "",
    preSelectedTickets: [],
  })

  // Statistics Value hook to monitor CS statistics
  const [statistics, setStatistics] = Value("cs-statistics", null)

  // Status dropdown change handler with direct DataTable refresh
  const handleStatusFilterChange = (values) => {
    if (values && values.length > 0) {
      setStatusFilter([values[0]])
    } else {
      setStatusFilter([{ value: null, label: "All" }])
    }

    // Force a deliberate refresh after a small delay
    setTimeout(() => {
      setRefreshKey(Date.now()) // Use timestamp for guaranteed change
    }, 50)
  }

  // Check for URL changes in a safe way
  useEffect(() => {
    if (typeof window !== "undefined") {
      const currentUrl = window.location.search

      // Only process if URL has changed and initial load is complete
      if (prevUrlRef.current !== currentUrl && initialLoadComplete) {
        prevUrlRef.current = currentUrl

        const searchParams = new URLSearchParams(currentUrl)
        const statusParam = searchParams.get("status")

        if (statusParam) {
          // Valid status values for direct mapping
          const validStatus = ["open", "pending", "resolved", "merged"]

          if (validStatus.includes(statusParam)) {
            setStatusFilter([
              {
                value: statusParam,
                label:
                  statusParam.charAt(0).toUpperCase() + statusParam.slice(1),
              },
            ])

            // Force refresh
            setTimeout(() => {
              setRefreshKey(Date.now())
            }, 50)
          }
        }
      }
    }
  })

  // Force a refresh whenever the filters change
  useEffect(() => {
    if (initialLoadComplete) {
      // Add a small delay to ensure all state updates are processed
      const refreshTimer = setTimeout(() => {
        setRefreshKey((prev) => prev + 1)
      }, 100)

      return () => clearTimeout(refreshTimer)
    }
  }, [
    initialLoadComplete,
    statusFilter[0]?.value,
    pendingReplyFilter[0]?.value,
    assignmentFilter[0]?.value,
    tagFilter.length,
    dateFilter.type,
    dateFilter.value,
    dateFilter.range?.startDate,
    dateFilter.range?.endDate,
  ])

  // Parse URL parameters on component mount
  useEffect(() => {
    // Don't process if we haven't loaded agents and tags yet
    if (agents.length <= 1 || tags.length === 0) return

    if (typeof window !== "undefined") {
      const searchParams = new URLSearchParams(window.location.search)

      // Process date range
      const dateRangeFrom = searchParams.get("date_range_from")
      const dateRangeTo = searchParams.get("date_range_to")

      if (dateRangeFrom && dateRangeTo) {
        // Parse dates (handle format like 10-10-2024)
        const startDate = parseCustomDateFormat(dateRangeFrom)
        const endDate = parseCustomDateFormat(dateRangeTo)

        if (startDate && endDate) {
          setDateFilter({
            type: "custom",
            range: { startDate, endDate },
          })
        }
      } else {
        // Check for preset date filter
        const presetDays = searchParams.get("date_preset")
        if (presetDays) {
          if (presetDays === "lifetime") {
            setDateFilter({ type: "preset", value: null })
          } else {
            const days = parseInt(presetDays, 10)
            if (!isNaN(days)) {
              setDateFilter({
                type: "preset",
                value: { days },
              })
            }
          }
        }
      }

      // Process reply status
      const replyStatus = searchParams.get("replyStatus")
      if (replyStatus) {
        setPendingReplyFilter([
          {
            value:
              replyStatus === "pending"
                ? true
                : replyStatus === "replied"
                ? false
                : null,
            label:
              replyStatus === "pending"
                ? "Pending Reply"
                : replyStatus === "replied"
                ? "Replied"
                : "All",
          },
        ])
      }

      // Process ticket status
      const status = searchParams.get("status")
      if (status) {
        // Valid status values for direct mapping
        const validStatus = ["open", "pending", "resolved", "merged"]

        if (validStatus.includes(status)) {
          setStatusFilter([
            {
              value: status,
              label: status.charAt(0).toUpperCase() + status.slice(1),
            },
          ])
        }
      }

      // Process agent filter
      const agentParam = searchParams.get("agent")
      if (agentParam) {
        if (agentParam === "unassigned") {
          setAssignmentFilter([{ value: "unassigned", label: "Unassigned" }])
        } else {
          // Try to find agent by name first (since URL shows names, not IDs)
          const foundAgent = agents.find(
            (a) =>
              a.label &&
              a.label.toLowerCase() ===
                agentParam.toLowerCase().replace(/\+/g, " ")
          )

          if (foundAgent) {
            setAssignmentFilter([foundAgent])
          }
        }
      }

      // Process tags
      const tagsParam = searchParams.get("tags")
      if (tagsParam && tags.length > 0) {
        const tagNames = tagsParam
          .split(",")
          .map((t) => t.trim().replace(/\+/g, " "))
        const selectedTags = tagNames
          .map((name) =>
            tags.find((t) => t.label.toLowerCase() === name.toLowerCase())
          )
          .filter(Boolean) // Remove any undefined entries

        if (selectedTags.length > 0) {
          setTagFilter(selectedTags)
        }
      }

      // Initial URL has been processed, update the ref
      prevUrlRef.current = window.location.search

      // Mark initial load as complete to enable normal filter changes
      setInitialLoadComplete(true)
    }
  }, [agents, tags])

  // Helper function to parse date format like "10-10-2024" (DD-MM-YYYY)
  const parseCustomDateFormat = (dateStr) => {
    const parts = dateStr.split("-")
    if (parts.length === 3) {
      // Assuming format is DD-MM-YYYY
      return new Date(parts[2], parts[1] - 1, parts[0])
    }
    return null
  }

  // Update URL when filters change
  useEffect(() => {
    // Skip URL updates during initial data load
    if (!initialLoadComplete || typeof window === "undefined") return

    const params = new URLSearchParams()

    // Add date filter
    if (dateFilter.type === "custom" && dateFilter.range) {
      const { startDate, endDate } = dateFilter.range
      if (startDate && endDate) {
        // Format as DD-MM-YYYY
        params.set("date_range_from", moment(startDate).format("DD-MM-YYYY"))
        params.set("date_range_to", moment(endDate).format("DD-MM-YYYY"))
      }
    } else if (dateFilter.type === "preset" && dateFilter.value) {
      const { days } = dateFilter.value
      if (days) {
        params.set("date_preset", days.toString())
      }
    }
    // Don't add any param for Lifetime (dateFilter.type === "preset" && dateFilter.value === null)

    // Add reply status
    const replyValue = pendingReplyFilter[0]?.value
    if (replyValue !== null) {
      params.set("replyStatus", replyValue === true ? "pending" : "replied")
    }

    // Add ticket status
    const statusValue = statusFilter[0]?.value
    if (statusValue !== null) {
      params.set("status", statusValue)
    }

    // Add agent
    const agentValue = assignmentFilter[0]?.value
    if (agentValue !== null) {
      if (agentValue === "unassigned") {
        params.set("agent", "unassigned")
      } else {
        // Find the agent to get their name
        const selectedAgent = agents.find((a) => a.value === agentValue)
        if (selectedAgent) {
          // Replace spaces with +
          params.set("agent", selectedAgent.label.replace(/\s+/g, "+"))
        }
      }
    }

    // Add tags
    if (tagFilter.length > 0) {
      const tagLabels = tagFilter
        .map((t) => t.label.replace(/\s+/g, "+"))
        .join(",")
      params.set("tags", tagLabels)
    }

    // Update URL with Vike's navigate
    const queryString = params.toString()
    const newPath =
      window.location.pathname + (queryString ? `?${queryString}` : "")

    // Only navigate if the URL is actually different to avoid unnecessary updates
    if (window.location.pathname + window.location.search !== newPath) {
      navigate(newPath, { keepScrollPosition: true })
    }
  }, [
    initialLoadComplete,
    dateFilter,
    statusFilter,
    assignmentFilter,
    pendingReplyFilter,
    tagFilter,
    agents,
  ])

  // Monitor statistics changes and refresh when data changes
  useEffect(() => {
    if (statistics?._lastFetch) {
      setRefreshKey((prev) => prev + 1)

      // Update tags from statistics if available
      if (statistics.tags && Array.isArray(statistics.tags)) {
        const newTagOptions = statistics.tags.map((tagData) => ({
          value: tagData.tag,
          label: tagData.tag,
        }))
        setTags(newTagOptions)
      }
    }
  }, [statistics?._lastFetch])

  // Fetch agents on component mount
  useEffect(() => {
    async function loadAgents() {
      try {
        const response = await GetAgents()
        if (response.result) {
          const agentOptions = [
            { value: null, label: "All Agents" },
            { value: "unassigned", label: "Unassigned" },
            ...response.data.map((agent) => ({
              value: agent.id,
              label: agent.name,
              email: agent.email,
            })),
          ]
          setAgents(agentOptions)
        }
      } catch (error) {
        showNotification({
          message: "Failed to load agents",
          className: "bg-admin_error text-white",
        })
      }
    }
    loadAgents()
  }, [])

  // Fetch tags on component mount
  useEffect(() => {
    async function loadTags() {
      try {
        const response = await GetTags()
        if (response.result) {
          const tagOptions = response.data.map((tag) => ({
            value: tag,
            label: tag,
          }))
          setTags(tagOptions)
        }
      } catch (error) {
        showNotification({
          message: "Failed to load tags",
          className: "bg-admin_error text-white",
        })
      }
    }
    loadTags()
  }, [])

  // Build match object for API - using useMemo to ensure it updates when filters change
  const requestFields = useMemo(() => {
    const match = {
      // Exclude spam and deleted tickets by default (added deleted check based on backend)
      isSpam: { $ne: true },
      deleted: { $ne: true }, // Explicitly ensure this matches the backend expectation
    }

    // Handle status filter
    const selectedStatus = statusFilter[0]?.value

    if (selectedStatus === "merged") {
      // Only show secondary tickets (tickets that have been merged into another ticket)
      match.mergedInto = { $exists: true, $ne: null }
    } else if (selectedStatus !== null) {
      // For regular status values, set the status and ALSO exclude merged tickets
      match.status = selectedStatus
      // Make sure we don't show merged tickets with regular statuses
      match.mergedInto = null // This ensures we only see non-merged tickets
    }
    // When selectedStatus is null (All), don't add any mergedInto filter
    // This allows both merged and non-merged tickets to appear

    // Handle assignment filter
    const selectedAssignment = assignmentFilter[0]?.value
    if (selectedAssignment === "unassigned") {
      match.assignedTo = null
    } else if (selectedAssignment !== null) {
      match.assignedTo = selectedAssignment
    }

    // Handle pendingReply filter
    const selectedPendingReply = pendingReplyFilter[0]?.value
    if (selectedPendingReply !== null) {
      match.pendingReply = selectedPendingReply
    }

    // Handle tag filter
    if (tagFilter.length > 0) {
      const selectedTags = tagFilter.map((tag) => tag.value)
      match.tags = { $in: selectedTags }
    }

    // Handle date filter
    if (dateFilter) {
      if (dateFilter.type === "preset" && dateFilter.value) {
        // Preset date (e.g., last 24h, last 7d)
        const { days } = dateFilter.value
        if (days) {
          const cutoffDate = moment().subtract(days, "days").toDate()
          match.timestamp = { $gte: cutoffDate }
        }
        // If dateFilter.value is null (lifetime option), we don't add any date restriction
      } else if (dateFilter.type === "custom" && dateFilter.range) {
        // Custom date range
        const { startDate, endDate } = dateFilter.range
        if (startDate && endDate) {
          // Set the time of endDate to 23:59:59 to include the entire day
          const adjustedEndDate = moment(endDate).endOf("day").toDate()
          match.timestamp = {
            $gte: moment(startDate).startOf("day").toDate(),
            $lte: adjustedEndDate,
          }
        }
      }
    }

    // Return in the format expected by the DataTable component
    return { match }
  }, [
    statusFilter,
    assignmentFilter,
    pendingReplyFilter,
    tagFilter,
    dateFilter,
  ])

  // Handle date filter change
  const handleDateFilterChange = (newDateFilter) => {
    setDateFilter(newDateFilter)
  }

  // Handle opening the assign dialog
  const handleOpenAssignDialog = (item) => {
    setAssignDialog({
      isOpen: true,
      ticketId: item.ticketID,
      ticketLabel: `${item.ticketID} - ${item.summary}`,
      currentAssignee: item.assignedTo?._id || null,
    })
  }

  // View handler
  const handleView = async (id) => {
    if (onSelectTicket) {
      onSelectTicket(id)
    }
  }

  // Handle clearing selection
  const handleClearSelection = () => {
    setSelectedItems([])
    setSelectedTicketData([])
  }

  // Handle select all change
  const handleSelectAll = (items, isSelected) => {
    if (isSelected) {
      setSelectedItems(items.map((item) => item.ticketID))
      setSelectedTicketData(items)
    } else {
      setSelectedItems([])
      setSelectedTicketData([])
    }
  }

  // Handle individual selection change
  const handleSelectItem = (item, isSelected) => {
    const itemId = typeof item === 'string' ? item : item.ticketID

    if (isSelected) {
      setSelectedItems((prev) => [...prev, itemId])
      if (typeof item === 'object') {
        setSelectedTicketData((prev) => [...prev, item])
      }
    } else {
      setSelectedItems((prev) => prev.filter((id) => id !== itemId))
      setSelectedTicketData((prev) => prev.filter((ticket) => ticket.ticketID !== itemId))
    }
  }

  // Handle deleting selected items
  const handleDeleteSelected = () => {
    if (selectedItems.length === 0) return

    showPromptDialog({
      title: "Confirm Delete",
      message: `Are you sure you want to delete ${selectedItems.length} ${
        selectedItems.length === 1 ? "ticket" : "tickets"
      }?`,
      confirmText: "Delete",
      onConfirm: async () => {
        try {
          const response = await DeleteTickets(selectedItems)

          if (response.result) {
            showNotification({
              message: `${selectedItems.length} ${
                selectedItems.length === 1 ? "ticket" : "tickets"
              } deleted successfully`,
              className: "bg-admin_primary text-admin_text_inv",
            })

            // Reset selection and refresh
            setSelectedItems([])
            setSelectedTicketData([])
            setRefreshKey((prev) => prev + 1)
          } else {
            showNotification({
              message: response.message || "Failed to delete tickets",
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          showNotification({
            message: "An error occurred while deleting",
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handle bulk tagging
  const handleBulkTag = () => {
    if (selectedItems.length === 0) return

    setTagDialog({
      isOpen: true,
      ticketId: null,
      ticketLabel: "",
      currentTags: [],
      ticketIds: selectedItems,
      ticketLabels: selectedTicketData.map(ticket =>
        `#${ticket.ticketID} - ${ticket.summary}`
      ),
    })
  }

  // Handle bulk spam toggle
  const handleBulkSpamToggle = async () => {
    if (selectedItems.length === 0) return

    try {
      // Determine if we should mark as spam or unmark
      // We'll assume marking as spam by default
      const promises = selectedItems.map(ticketId =>
        UpdateTicket({ ticketId, isSpam: true })
      )

      await Promise.all(promises)

      showNotification({
        message: `${selectedItems.length} tickets marked as spam`,
        className: "bg-admin_primary text-admin_text_inv",
      })

      // Reset selection and refresh
      setSelectedItems([])
      setSelectedTicketData([])
      setRefreshKey((prev) => prev + 1)
    } catch (error) {
      showNotification({
        message: "Failed to update spam status",
        className: "bg-admin_error text-white",
      })
    }
  }

  // Handle bulk merge
  const handleBulkMerge = () => {
    if (selectedItems.length < 2) {
      showNotification({
        message: "Please select at least 2 tickets to merge",
        className: "bg-admin_error text-white",
      })
      return
    }

    // Prepare tickets for merge dialog
    const mergeTickets = selectedTicketData.map(ticket => ({
      ticketID: ticket.ticketID,
      label: `${ticket.ticketID} - ${ticket.summary}`,
      summary: ticket.summary,
      from: ticket.author,
      created: ticket.timestamp,
    }))

    setMergeDialog({
      isOpen: true,
      initialTicketId: null,
      initialTicketLabel: "",
      preSelectedTickets: mergeTickets,
    })
  }


  // Updated table header content with filters - adjusted grid for date filter
  const tableHeader = (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-2 mt-2 mb-4">
      {/* Date Range filter - new filter with variant */}
      <div className="flex flex-col">
        <Typography variant="admin_txt2_dim" className="mb-0.5">
          Date Range
        </Typography>

        <DateFilterDropdown
          onChange={handleDateFilterChange}
          variant="admin"
          value={dateFilter}
          highlightOnSelection={true}
          showLabel={false}
          highlightResetValue="lifetime"
          className="w-full"
        />
      </div>

      {/* Pending Reply filter */}
      <div className="flex flex-col">
        <Typography variant="admin_txt2_dim" className="mb-0.5">
          Reply Status
        </Typography>

        <DropDownSelect
          variant="admin"
          options={[
            { value: null, label: "All" },
            { value: true, label: "Pending Reply" },
            { value: false, label: "Replied" },
          ]}
          value={pendingReplyFilter}
          onChange={(values) => {
            if (values && values.length > 0) {
              setPendingReplyFilter([values[0]])
            } else {
              setPendingReplyFilter([{ value: null, label: "All" }])
            }
          }}
          className="w-full"
          placeholder="Filter by reply status"
          showCheckbox={false}
          showSelectionTick={true}
          highlightOnSelection={true}
          highlightResetValue={null}
        />
      </div>

      {/* Status filter */}
      <div className="flex flex-col">
        <Typography variant="admin_txt2_dim" className="mb-0.5">
          Ticket Status
        </Typography>

        <DropDownSelect
          variant="admin"
          options={[
            { value: null, label: "All" },
            { value: "open", label: "Open" },
            { value: "pending", label: "Pending" },
            { value: "resolved", label: "Resolved" },
            { value: "merged", label: "Merged" },
          ]}
          value={statusFilter}
          onChange={handleStatusFilterChange}
          className="w-full"
          placeholder="Filter by ticket status"
          showCheckbox={false}
          showSelectionTick={true}
          highlightOnSelection={true}
          highlightResetValue={null}
        />
      </div>

      {/* Assignment filter */}
      <div className="flex flex-col">
        <Typography variant="admin_txt2_dim" className="mb-0.5">
          Assigned Agent
        </Typography>

        <DropDownSelect
          variant="admin"
          options={agents}
          value={assignmentFilter}
          onChange={(values) => {
            if (values && values.length > 0) {
              setAssignmentFilter([values[0]])
            } else {
              setAssignmentFilter([{ value: null, label: "All Agents" }])
            }
          }}
          className="w-full"
          searchEnabled
          placeholder="Filter by assigned agent"
          showCheckbox={false}
          showSelectionTick={true}
          highlightOnSelection={true}
          highlightResetValue={null}
        />
      </div>

      {/* Tags filter */}
      <div className="flex flex-col">
        <Typography variant="admin_txt2_dim" className="mb-0.5">
          Ticket Tags
        </Typography>

        <DropDownSelect
          variant="admin"
          options={tags}
          value={tagFilter}
          onChange={(values) => {
            setTagFilter(values || [])
          }}
          className="w-full"
          searchEnabled
          multiSelection
          placeholder="Filter by tags"
          highlightOnSelection={true}
        />
      </div>
    </div>
  )

  // Style overrides for the DataTable
  const styleOverrides = {
    table: "w-full table-fixed",
    header: "w-full flex justify-between items-end gap-2 mt-6 mb-4",
    searchInput: "absolute top-4 right-4 text-text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1",
    row: "h-[60px] text-admin_text text-sm xl:text-base font-normal",

    getRowStyle: (index, item) => {
      let classes = "border-b border-admin_bordered/60"

      // Check if this item is selected
      if (item && selectedItems.includes(item.ticketID)) {
        classes += " bg-admin_canvas" // Selected row style
      } else {
        // Keep the alternating row styles if not selected
        classes +=
          index % 2 === 0 ? " bg-admin_paper" : " bg-admin_paper bg-opacity-65"
      }

      return classes
    },

    getCellStyle: (itemClassName) => {
      return twMerge(
        "px-2 py-3", // Increased y-padding
        itemClassName || ""
      )
    },

    // Keep headers aligned with content
    getHeaderCellStyle: (headerClassName) => {
      return twMerge("px-2 py-2", headerClassName || "")
    },
  }

  // Column definitions with selection checkbox - using useMemo to access statusFilter
  const columns = useMemo(
    () => [
      {
        id: "select",
        headerClassName: "w-[4%]",
        header: (value, items) => (
          <Checkbox
            isOn={items.length > 0 && selectedItems.length === items.length}
            onChange={(isSelected) => handleSelectAll(items, isSelected)}
            variant="admin"
            className="cursor-pointer"
          />
        ),
        render: (value, item) => (
          <Checkbox
            isOn={selectedItems.includes(item.ticketID)}
            onChange={(isSelected) =>
              handleSelectItem(item, isSelected)
            }
            variant="admin"
          />
        ),
      },
      {
        id: "status",
        title: "Status",
        headerClassName: "w-[10%]",
        render: (_, item) => {
          // Show merged status for secondary tickets (tickets that have been merged into another)
          if (item.mergedInto) {
            return (
              <span
                className="text-white text-xs bg-admin_warning rounded-full px-2 py-0.5"
              >
                Merged
              </span>
            )
          }

          const statusConfig = {
            open: {
              color: "text-white bg-admin_neutral_3",
              label: "Open",
            },
            pending: {
              color: "text-white bg-admin_progress",
              label: "Pending",
            },
            resolved: {
              color: "text-white bg-admin_success",
              label: "Resolved",
            },
          }
          const { color, label } = statusConfig[item.status] || {
            color: "text-admin_text_secondary bg-admin_neutral_3/5",
            label: item.status,
          }

          return (
            <span className={twMerge(color, "text-xs rounded-full px-2 py-0.5")}>
              {label}
            </span>
          )
        },
      },
      {
        id: "summary",
        title: "Summary",
        headerClassName: "w-[16%]",
        render: (_, item) => (
          <Tooltip
            display="block"
            content="Click to view ticket details"
          >
            <div
              className="relative group"
              onClick={() => handleView(item.ticketID)}
            >
              <span
                className="block truncate text-admin_text hover:text-admin_primary font-semibold group-hover:underline cursor-pointer transition-colors"
              >
                {item.summary && item.summary.length > 50
                  ? `${item.summary.substring(0, 50)}...`
                  : item.summary}
              </span>
            </div>
          </Tooltip>
        ),
      },
      {
        id: "tags",
        title: "Tags",
        headerClassName: "w-[14%]", // Same as email
        render: (_, item) => (
          <div className="relative flex flex-col items-start gap-1">
            {item.tags && item.tags.length > 0 ? (
              <>
                {/* Show up to 1 tag inline */}
                {item.tags.slice(0, 1).map((tag, index) => (
                  <span
                    key={index}
                    className={`text-xs rounded-full whitespace-nowrap px-2 py-0.5 ${getTagColor(tag)}`}
                  >
                    {tag}
                  </span>
                ))}

                {/* If there are more tags, show a +X indicator with tooltip */}
                {item.tags.length > 1 && (
                  <Tooltip
                    content={
                      <div className="relative p-1 flex flex-col gap-1">
                        {item.tags.slice(1).map((tag, i) => (
                          <span key={i} className={`text-xs rounded-full whitespace-nowrap px-2 py-0.5 ${getTagColor(tag)}`}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    }
                  >
                    <span className="absolute -bottom-4 text-xs text-admin_text_secondary bg-admin_neutral_1/50 rounded-full whitespace-nowrap cursor-pointer leading-none px-2 py-0.5">
                      +{item.tags.length - 1}
                    </span>
                  </Tooltip>
                )}
              </>
            ) : (
              <span className="text-admin_text_secondary/70">
                No tags
              </span>
            )}
          </div>
        ),
      },
      {
        id: "author",
        title: "User",
        headerClassName: "w-[18%]",
        render: (_, item) => (
          <Tooltip
            display="block"
            content={item.author}
          >
            <span className="block truncate">
              {item.author}
            </span>
          </Tooltip>
        ),
      },
      {
        id: "timestamp",
        title: "Time",
        headerClassName: "w-[13%]",
        render: (_, item) => (
          <div className="flex flex-col mt-[11px]">
            <span className="xl:whitespace-nowrap leading-tight">
              {formatDate(item.timestamp)}
            </span>

            <span className="text-xs text-admin_text_secondary/70 leading-none">
              {getRelativeTimeString(item.timestamp)}
            </span>
          </div>
        ),
      },
      {
        id: "ticketID",
        title: "Ticket #",
        headerClassName: "w-[14%]",
        render: (_, item) => (
          <span>
            {item.ticketID}
          </span>
        ),
      },
      {
        id: "assignedTo",
        title: "Assignee",
        headerClassName: "w-[12%] whitespace-nowrap",
        render: (_, item) => (
          <Tooltip
            display="block"
            content="Click to assign ticket"
          >
            <div
              className="block truncate cursor-pointer hover:text-admin_primary hover:underline transition-colors"
              onClick={() => handleOpenAssignDialog(item)}
            >
              {item.assignedTo?.name || (
                <span className="text-admin_text_secondary/70">
                  Unassigned
                </span>
              )}
            </div>
          </Tooltip>
        ),
      },
      {
        id: "actions",
        title: "Actions",
        headerClassName: "w-[13%] text-center",
        render: (_, item) => (
          <div className="flex gap-1">
            {item.pendingReply ? (
              // Solid red chip for "Pending Reply"
              <Button
                variant="admin_primary_red"
                className="h-auto text-sm font-medium px-3 py-1 mx-auto"
                onClick={() => handleView(item.ticketID)}
              >
                Respond
              </Button>
            ) : (
              // Regular eye icon for normal tickets
              <Button
                variant="admin_icon"
                className="p-1.5 text-admin_text_secondary hover:text-admin_primary transition-colors mx-auto"
                onClick={() => handleView(item.ticketID)}
                title="View ticket details"
              >
                <EyeIcon className="w-5 h-5" />
              </Button>
            )}
          </div>
        ),
      },
    ],
    [statusFilter, selectedItems]
  ) // Include any dependencies like statusFilter and selectedItems

  // Table footer content - action buttons
  const tableFooter = selectedItems.length > 0 && (
    <div className="flex justify-end items-center p-3 bg-admin_paper rounded">
      <div className="flex items-center gap-1 mr-4">
        <Button
          variant="admin_text_warning"
          className="mr-2"
          onClick={handleClearSelection}
        >
          Deselect
        </Button>

        <Typography variant="admin_body">
          <span className="font-bold">{selectedItems.length}</span>{" "}
          {selectedItems.length === 1 ? "ticket" : "tickets"} selected
        </Typography>
      </div>

      <Button
        variant="admin_secondary"
        className="mr-3"
        onClick={handleBulkTag}
      >
        <div className="flex items-center">
          <TagIcon className="w-5 h-5 mr-2" />
          Tag
        </div>
      </Button>

      <Button
        variant="admin_secondary"
        className="mr-3"
        onClick={handleBulkSpamToggle}
      >
        <div className="flex items-center">
          <ShieldExclamationIcon className="w-5 h-5 mr-2" />
          Mark as Spam
        </div>
      </Button>

      {selectedItems.length > 1 && (
        <Button
          variant="admin_secondary"
          className="mr-3"
          onClick={handleBulkMerge}
        >
          <div className="flex items-center">
            <ArrowPathIcon className="w-5 h-5 mr-2" />
            Merge
          </div>
        </Button>
      )}

      <Button variant="admin_error" onClick={handleDeleteSelected}>
        <div className="flex items-center">
          <TrashIcon className="w-5 h-5 mr-2" />
          Delete
        </div>
      </Button>
    </div>
  )

  return (
    <div className="p-4">
      <div className="w-full flex items-center justify-between mb-5">
        <Typography
          component="h1"
          variant="admin_h3"
        >
          Support Tickets
        </Typography>

        <div className="flex justify-end gap-3">
          <Button
            variant="admin_secondary"
            onClick={() => setIsRestoreDialogOpen(true)}
          >
            Restore
          </Button>

          <Button
            variant="admin_error"
            className="flex items-center"
            onClick={() => setIsSpamDialogOpen(true)}
          >
            <ShieldExclamationIcon className="w-5 h-5 mr-1" /> Spam
          </Button>

          <Button
            variant="admin_primary"
            className="whitespace-nowrap flex items-center"
            onClick={() => setAddTicketDialog({ isOpen: true })}
          >
            <PlusCircleIcon className="w-5 h-5 mr-1" /> Add Ticket
          </Button>
        </div>
      </div>

      {/* Main data table with new tableHeader and tableFooter props */}
      <DataTable
        variant="admin_browser"
        apiMethod={GetTickets}
        requestFields={requestFields}
        tableHeader={tableHeader}
        tableFooter={tableFooter}
        searchField="summary author ticketID"
        sort={{ timestamp: -1 }}
        renders={columns}
        refreshKey={refreshKey}
        preferredHeight="65vh"
        itemHeight="h-auto"
        noData={<Typography variant="admin_body">No tickets found</Typography>}
        styleOverrides={styleOverrides}
        localUserPreferencesKey="ui.ticketBrowser"
      />


      {/* Dialogs */}
      <AssignDialog
        isOpen={assignDialog.isOpen}
        ticketId={assignDialog.ticketId}
        ticketLabel={assignDialog.ticketLabel}
        currentAssignee={assignDialog.currentAssignee}
        onClose={() =>
          setAssignDialog({
            isOpen: false,
            ticketId: null,
            ticketLabel: "",
            currentAssignee: null,
          })
        }
        onAssigned={() => {
          setRefreshKey((k) => k + 1)
          setAssignDialog({
            isOpen: false,
            ticketId: null,
            ticketLabel: "",
            currentAssignee: null,
          })
        }}
      />

      <AddTicketDialog
        isOpen={addTicketDialog.isOpen}
        onClose={() => setAddTicketDialog({ isOpen: false })}
        onTicketCreated={() => {
          setRefreshKey((prev) => prev + 1)
          showNotification({
            message: "Ticket created successfully",
            className: "bg-admin_primary text-admin_text_inv",
          })
        }}
      />

      <RestoreTicketsDialog
        isOpen={isRestoreDialogOpen}
        onClose={() => setIsRestoreDialogOpen(false)}
        parentRefreshKey={refreshKey}
        onTicketsRestored={() => setRefreshKey((prev) => prev + 1)}
      />

      <SpamTicketsDialog
        isOpen={isSpamDialogOpen}
        onClose={() => setIsSpamDialogOpen(false)}
        parentRefreshKey={refreshKey}
        onTicketsUpdated={() => setRefreshKey((prev) => prev + 1)}
        onSelectTicket={onSelectTicket}
      />

      {/* Tag Dialog */}
      <TagDialog
        isOpen={tagDialog.isOpen}
        ticketId={tagDialog.ticketId}
        ticketLabel={tagDialog.ticketLabel}
        currentTags={tagDialog.currentTags}
        ticketIds={tagDialog.ticketIds}
        ticketLabels={tagDialog.ticketLabels}
        onClose={() => setTagDialog({
          isOpen: false,
          ticketId: null,
          ticketLabel: "",
          currentTags: [],
          ticketIds: [],
          ticketLabels: []
        })}
        onUpdated={() => {
          setRefreshKey((prev) => prev + 1)
          setSelectedItems([])
          setSelectedTicketData([])
          setTagDialog({
            isOpen: false,
            ticketId: null,
            ticketLabel: "",
            currentTags: [],
            ticketIds: [],
            ticketLabels: []
          })
        }}
      />

      {/* Merge Dialog */}
      <MergeTickets
        isOpen={mergeDialog.isOpen}
        initialTicketId={mergeDialog.initialTicketId}
        initialTicketLabel={mergeDialog.initialTicketLabel}
        preSelectedTickets={mergeDialog.preSelectedTickets}
        onClose={(merged) => {
          setMergeDialog({
            isOpen: false,
            initialTicketId: null,
            initialTicketLabel: "",
            preSelectedTickets: []
          })
          if (merged) {
            setRefreshKey((prev) => prev + 1)
            setSelectedItems([])
            setSelectedTicketData([])
          }
        }}
      />
    </div>
  )
}
